import React from 'react';
import ContactForm from '../components/ContactForm';
import site from '../content/siteContent';

export default function Contact() {
  return (
    <div className="min-h-screen bg-sand/5">
      {/* Hero Section */}
      <div className="bg-wood text-sand py-16">
        <div className="container">
          <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
          <p className="text-sand/90 max-w-2xl text-content">
            Have a project in mind? We'd love to hear from you. Send us a message and we'll respond
            as soon as possible.
          </p>
        </div>
      </div>

      <div className="container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-8">
            {/* Phone Numbers */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Get in Touch</h3>
              <div className="space-y-4">
                {site.phones.map((phone, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="p-2 bg-wood/10 rounded-lg">
                      <svg
                        className="w-5 h-5 text-wood"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </div>
                    <div>
                      <a
                        href={`tel:${phone.number}`}
                        className="text-lg font-medium hover:text-wood transition-colors"
                      >
                        {phone.display}
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp Button */}
              <a
                href={`https://wa.me/${site.phones[0].number.replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="mt-6 flex items-center justify-center w-full btn btn-wood gap-2 group"
              >
                <svg
                  className="w-5 h-5 group-hover:scale-110 transition-transform"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.1.824z" />
                </svg>
                Chat on WhatsApp
              </a>
            </div>

            {/* Business Hours Card */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Business Hours</h3>
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-wood/10 rounded-lg">
                  <svg
                    className="w-5 h-5 text-wood"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Monday - Saturday</div>
                  <div className="text-neutralText mt-1">{site.hours}</div>
                </div>
              </div>
            </div>

            {/* Address Card */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Visit Our Showroom</h3>
              <div className="space-y-2 text-content">
                <p className="font-medium">{site.name}</p>
                <address className="not-italic text-neutralText leading-relaxed">
                  {site.address.street},<br />
                  {site.address.area},<br />
                  {site.address.city},<br />
                  {site.address.subArea},<br />
                  {site.address.region} - {site.address.postalCode}
                </address>
              </div>
              <a
                href={site.address.mapUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center text-wood hover:text-wood-dark transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Get Directions
              </a>
            </div>
          </div>

          {/* Contact Form and Map */}
          <div className="lg:col-span-2 space-y-8">
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-6">Send us a Message</h3>
              <ContactForm />
            </div>

            <div className="card overflow-hidden">
              <iframe
                title="map"
                src={site.address.mapEmbed}
                width="100%"
                height="450"
                className="border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
